import React, { ReactNode, useMemo } from 'react';
import { GlobalStyles } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';

import { adminTokens as t } from '@libs/components/src/lib/AdminDesignSystem/tokens';

const { accent, border: line, canvas: surface, font: fontFamily } = t;

// Share visual tokens while preserving application-specific component behavior.
const options: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: { main: accent, dark: t.accentDark, light: t.accentLight, contrastText: '#FFFFFF' },
    secondary: { main: '#202024', dark: '#000000', light: '#68686F', contrastText: '#FFFFFF' },
    background: { default: surface, paper: '#FFFFFF' },
    text: { primary: '#202024', secondary: '#68686F' },
    divider: line,
  },
  shape: { borderRadius: 2 },
  typography: {
    fontFamily,
    h1: { fontSize: 30, fontWeight: 700, letterSpacing: '-0.8px' },
    h2: { fontSize: 26, fontWeight: 700, letterSpacing: '-0.6px' },
    h3: { fontSize: 18, fontWeight: 600, letterSpacing: '-0.3px' },
    button: { fontWeight: 600, textTransform: 'none' },
  },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 2, border: `1px solid ${line}`, boxShadow: 'none' } } },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          // AppCard's legacy sx sets pb: 0. Keep spacing on both sides of the rule.
          '&&': { padding: '16px 24px', borderBottom: `1px solid ${line}` },
          '& .MuiCardHeader-action': { alignSelf: 'center', marginBottom: 0 },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 2, minHeight: 40, padding: '7px 16px', fontSize: 13 },
        sizeSmall: { minHeight: 28, padding: '4px 8px', fontSize: 12 },
        outlined: { borderColor: '#D4D4D8', '&:hover': { borderColor: accent, backgroundColor: surface } },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 2, backgroundColor: '#FFFFFF', fontSize: 13 },
        notchedOutline: { borderColor: '#D4D4D8' },
      },
    },
    MuiCardContent: { styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } } },
    MuiFormLabel: { styleOverrides: { root: { fontSize: 13, lineHeight: 1.5 } } },
    MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0, lineHeight: 1.6 } } },
    MuiButtonBase: {
      styleOverrides: { root: { '&.Mui-focusVisible': { outline: `2px solid ${accent}`, outlineOffset: 3 } } },
    },
    MuiTableContainer: { styleOverrides: { root: { borderRadius: 2 } } },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: line, fontVariantNumeric: 'tabular-nums' },
        head: { backgroundColor: surface, color: '#68686F', fontSize: 12, fontWeight: 600 },
        body: { fontSize: 13 },
      },
    },
    MuiTab: { styleOverrides: { root: { minHeight: 48, fontSize: 14, fontWeight: 600 } } },
    MuiTabs: { styleOverrides: { root: { borderBottom: `1px solid ${line}` }, indicator: { height: 2 } } },
    MuiDialog: { styleOverrides: { paper: { borderRadius: 8, border: `1px solid ${line}` } } },
    MuiDialogTitle: {
      styleOverrides: { root: { padding: '24px', fontWeight: 700, borderBottom: `1px solid ${line}` } },
    },
    MuiDialogActions: { styleOverrides: { root: { padding: '16px 24px', borderTop: `1px solid ${line}` } } },
    MuiMenu: {
      styleOverrides: { paper: { border: `1px solid ${line}`, borderRadius: 6, boxShadow: 'none' } },
    },
    MuiMenuItem: { styleOverrides: { root: { fontSize: 13, minHeight: 40 } } },
  },
};

const workspaceStyles = {
  '.defaultLayoutWrapper': { backgroundColor: surface, fontFamily },
  '.app-sidebar': {
    borderColor: line,
    '& > div': { backgroundColor: '#FFFFFF' },
    '& .menu-vertical-item, & .menu-vertical-collapse': {
      borderRadius: '4px !important',
      minHeight: 42,
      transition: 'background-color 150ms ease',
      '&:hover': { backgroundColor: surface },
      '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: -2 },
      '& .nav-item-text': { color: '#55555D !important', fontSize: 13 },
      '& .nav-item-icon, & .nav-item-icon-arrow': { color: '#85858D !important', width: 20, height: 20 },
      '&.active, &.leaf-menu.active': {
        backgroundColor: `${t.selected} !important`,
        boxShadow: 'none',
        '& .nav-item-text, & .nav-item-icon': { color: `${accent} !important`, fontWeight: 600 },
      },
      '&.child-selected .nav-item-text': { color: '#202024 !important', fontWeight: 600 },
    },
  },
  '.app-bar': {
    borderColor: line,
    '& .MuiToolbar-root': { paddingLeft: 32, paddingRight: 32 },
    '& .MuiButton-root': { color: '#55555D', borderColor: line },
  },
  '.app-content': {
    padding: '32px !important',
    minWidth: 0,
    '& .apps-page-heading, & .app-page-header': {
      paddingBottom: 24,
      marginBottom: 24,
      borderBottom: `1px solid ${line}`,
    },
    '& .apps-page-title, & .app-component-title': { fontSize: 30, fontWeight: 700, letterSpacing: '-0.6px' },
    '& .apps-page-description, & .app-component-description': { marginTop: 8, lineHeight: 1.7, whiteSpace: 'pre-line' },
    '& .app-page-header': { alignItems: 'flex-end', flexWrap: 'wrap', gap: 12 },
    '& .app-component-description': { fontSize: 14 },
    '& .app-container-wrapper, & .list-page-layout': { minWidth: 0 },
    '& .apps-page-heading > .MuiStack-root': { gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' },
    '& .apps-page-heading .MuiTypography-root, & .app-page-header .MuiTypography-root': { overflowWrap: 'anywhere' },
    '& .app-page-header > .MuiBox-root:last-child > div': { display: 'flex', gap: 8, flexWrap: 'wrap' },
    '& .MuiCardHeader-title': { fontSize: 16, fontWeight: 700, lineHeight: 1.5 },
    '& .MuiCardHeader-content, & .MuiGrid-item': { minWidth: 0 },
    '& .MuiTableContainer-root': { maxWidth: '100%', overflowX: 'auto' },
    '& .MuiTabs-root': { minHeight: 48, backgroundColor: '#FFFFFF' },
    '& .MuiTab-root': { textTransform: 'none' },
    '& .detail-layout': {
      '& .app-page-header': { borderBottom: `2px solid ${accent}`, paddingBottom: 20 },
      '& .detail-section': { borderRadius: 2 },
      '& .MuiFormControl-root': { minWidth: 0, maxWidth: '100%' },
      '& .MuiFormControlLabel-root': { alignItems: 'flex-start', marginRight: 16 },
      '& .MuiFormControlLabel-label': { paddingTop: 8, overflowWrap: 'anywhere' },
      '& .MuiCardContent-root > .MuiStack-root': { minWidth: 0 },
      '& .MuiGrid-item > .MuiCard-root': { height: '100%' },
    },
    '& .MuiDataGrid-root': {
      borderColor: line,
      borderRadius: 4,
      backgroundColor: '#FFFFFF',
      fontSize: 13,
      fontVariantNumeric: 'tabular-nums',
      '& .MuiDataGrid-columnHeaders': { backgroundColor: surface, borderColor: line },
      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 600, color: '#68686F' },
      '& .MuiDataGrid-cell': { borderColor: '#EEEEF0' },
      '& .MuiDataGrid-row:hover': { backgroundColor: surface },
      '& .MuiDataGrid-row.Mui-selected': { backgroundColor: t.selected },
      '& .MuiDataGrid-footerContainer': { borderColor: line },
    },
  },
  '@media (max-width: 899px)': {
    '.app-content': {
      padding: '20px !important',
      '& .apps-page-title, & .app-component-title': { fontSize: 24 },
      '& .MuiCardHeader-root': { padding: '16px' },
      '& .MuiCardContent-root': { padding: 16, '&:last-child': { paddingBottom: 16 } },
    },
    '.app-bar .MuiToolbar-root': { paddingLeft: 16, paddingRight: 16 },
    '.apps-page-heading > .MuiStack-root': { flexWrap: 'wrap', gap: 12 },
  },
  '@media (max-width: 599px)': {
    '.app-content .app-page-header': { alignItems: 'stretch' },
  },
  '@media (prefers-reduced-motion: reduce)': {
    '.app-sidebar, .mainContent, .app-sidebar .MuiListItem-root': { transition: 'none' },
  },
};

export default function ChannelAdminTheme({ children }: { children: ReactNode }) {
  const parentTheme = useTheme();
  const { pathname } = useLocation();
  const theme = useMemo(() => createTheme(parentTheme, options), [parentTheme]);
  // Product editors and authentication retain their independent visual systems.
  if (
    /^\/(auth|editor|editor-open)(\/|$)/.test(pathname) ||
    pathname === '/myPod/new' ||
    pathname.includes('integratedEditor')
  ) {
    return <>{children}</>;
  }
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles styles={workspaceStyles} />
      {children}
    </ThemeProvider>
  );
}
